import { useCallback, useEffect, useRef } from 'react';

/*
How Infinite pagination works:
1) On mount, the useEffect begins observing the ref

2) Upon observing the ref created the observer should trigger the callback which calls the onScrollTo function,

3) Then the intersectionObserver is replaced and placed into the new tableRef created in the insightsTablePage

Source: Followed some similar logic from EoC and Confluence's infinite scroll implementation.  https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/confluence/next/packages/infinite-scroll/src/InfiniteScroll.tsx
*/

/**
 * UseInfiniteScroll is a hook that observes an element and calls a function when it is intersecting.
 * This is useful for infinite scrolling.
 *
 * UseInfiniteScroll also accepts a type parameter that allows you to specify the type of the element being observed.
 *
 * @param onScrollTo: Function that is called when the element is intersecting
 * @returns loadMoreRef: Ref to the element that is being observed
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLElement>({
	onScrollTo,
}: {
	onScrollTo: () => void;
}) {
	const loadMoreRef = useRef<T | null>(null);

	// This function is called by the IntersectionObserver API when "loaderTarget" UI element
	// changes visibility.  If it has become visible (typically by user scrolling), call our
	// scroll handler and the parent will attempt to load more data
	const fetchMoreCallback = useCallback(
		(loadMoreEntry: IntersectionObserverEntry[]) => {
			// Sometimes based on an unknown render race condition, the previous IntersectionObserver is not
			// properly cleared before the next one is observed, which causes the 0th indexed IntersectionObserver
			// to never be intersecting, which causes the onScrollTo callback to not be called when it should be.
			// To fix this just get rid of the old observer if it is not properly cleaned up.
			if (loadMoreEntry.length > 1) {
				loadMoreEntry.shift();
			}

			if (loadMoreEntry?.[0].isIntersecting) {
				onScrollTo();
			}
		},
		[onScrollTo],
	);

	useEffect(() => {
		const intersectionObserver = new IntersectionObserver(fetchMoreCallback);
		const loadMoreRefCurrent = loadMoreRef.current;
		if (loadMoreRef.current) {
			intersectionObserver.observe(loadMoreRef.current);
		}

		return () => {
			if (loadMoreRefCurrent) {
				intersectionObserver.unobserve(loadMoreRefCurrent);
			}
			intersectionObserver.disconnect();
		};
	}, [loadMoreRef, fetchMoreCallback]);

	return loadMoreRef;
}
